import { useMemo } from 'react';
import { Activity, Download, PackageCheck, Settings2, Target, WalletCards } from 'lucide-react';
import type { EChartsCoreOption } from 'echarts/core';
import Chart from '../components/Chart';
import ProgressCard from '../components/ProgressCard';
import { buildTrend, targetForPeriod } from '../lib/analytics';
import { exportKpis } from '../lib/exporter';
import { formatCompactCurrency, formatNumber, formatPercent } from '../lib/format';
import type { PeriodMode, SalesRecord, SummaryMetrics, TargetConfig } from '../types';

interface KpiViewProps {
  records: SalesRecord[];
  summary: SummaryMetrics;
  targets: TargetConfig;
  periodMode: PeriodMode;
  dateLabel: string;
  onPeriodMode: (mode: PeriodMode) => void;
  onOpenTargets: () => void;
}

const axisText = { color: '#7d899d', fontFamily: 'Inter, Microsoft YaHei, sans-serif' };
const periodLabel = { month: '月度', quarter: '季度', year: '年度' } as const;

export default function KpiView({ records, summary, targets, periodMode, dateLabel, onPeriodMode, onOpenTargets }: KpiViewProps) {
  const trend = useMemo(() => buildTrend(records, targets.targetProduct), [records, targets.targetProduct]);
  const salesTarget = targetForPeriod(targets.annualSalesTarget, periodMode);
  const productTarget = targetForPeriod(targets.annualProductQuantityTarget, periodMode);
  const salesRate = salesTarget ? (summary.sales / salesTarget) * 100 : 0;
  const productRate = productTarget ? (summary.targetProductQuantity / productTarget) * 100 : 0;
  const terminalRate = targets.activeTerminalTarget ? (summary.terminals / targets.activeTerminalTarget) * 100 : 0;

  const completionOption = useMemo<EChartsCoreOption>(() => ({
    color: ['#2563eb'],
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: '#172033', borderWidth: 0, textStyle: { color: '#fff' }, valueFormatter: (value: unknown) => formatPercent(Number(value)) },
    grid: { left: 8, right: 36, top: 16, bottom: 8, containLabel: true },
    xAxis: { type: 'value', max: Math.max(120, Math.ceil(Math.max(salesRate, productRate, terminalRate) / 20) * 20), axisLabel: { ...axisText, formatter: '{value}%' }, splitLine: { lineStyle: { color: '#eef2f7', type: 'dashed' } } },
    yAxis: { type: 'category', data: ['活跃终端', '重点单品', '总销售额'], axisLine: { show: false }, axisTick: { show: false }, axisLabel: axisText },
    series: [{
      type: 'bar',
      barWidth: 18,
      showBackground: true,
      backgroundStyle: { color: '#eef2f7', borderRadius: 9 },
      label: { show: true, position: 'right', formatter: (params: { value: number }) => formatPercent(params.value, 0), color: '#41506a', fontWeight: 700 },
      itemStyle: {
        borderRadius: 9,
        color: (params: { dataIndex: number }) => ['#7868e6', '#18a394', '#2563eb'][params.dataIndex],
      },
      data: [terminalRate, productRate, salesRate].map((value) => Number(value.toFixed(2))),
    }],
  }), [productRate, salesRate, terminalRate]);

  const salesTrendOption = useMemo<EChartsCoreOption>(() => {
    const perBucketTarget = trend.length ? salesTarget / trend.length : 0;
    return {
      color: ['#2563eb', '#9aa7ba'],
      tooltip: { trigger: 'axis', backgroundColor: '#172033', borderWidth: 0, textStyle: { color: '#fff' }, valueFormatter: (value: unknown) => formatCompactCurrency(Number(value)) },
      legend: { top: 0, right: 0, icon: 'circle', itemWidth: 8, itemHeight: 8, textStyle: axisText },
      grid: { left: 18, right: 18, top: 42, bottom: 8, containLabel: true },
      xAxis: { type: 'category', data: trend.map((item) => item.label), axisLine: { lineStyle: { color: '#e8edf5' } }, axisTick: { show: false }, axisLabel: axisText },
      yAxis: { type: 'value', axisLabel: { ...axisText, formatter: (value: number) => `${Math.round(value / 10000)}万` }, splitLine: { lineStyle: { color: '#eef2f7', type: 'dashed' } } },
      series: [
        { name: '实际销售额', type: 'bar', barMaxWidth: 20, itemStyle: { borderRadius: [6, 6, 0, 0] }, data: trend.map((item) => Number(item.sales.toFixed(2))) },
        { name: '均衡进度线', type: 'line', smooth: true, symbol: 'none', lineStyle: { type: 'dashed', width: 2 }, data: trend.map(() => perBucketTarget) },
      ],
    };
  }, [salesTarget, trend]);

  const exportCurrent = () => void exportKpis([{
    period: `${periodLabel[periodMode]} ${dateLabel}`,
    sales: summary.sales,
    salesTarget,
    productQuantity: summary.targetProductQuantity,
    productTarget,
    terminals: summary.terminals,
    terminalTarget: targets.activeTerminalTarget,
  }], targets, dateLabel);

  return (
    <div className="view-stack">
      <section className="section-heading heading-row standalone-heading">
        <div>
          <span className="eyebrow">KPI 追踪</span>
          <h2>目标、进度与偏差一屏跟进</h2>
          <p>按当前数据最新日期定位周期，金额与单品目标由年度值自动折算。</p>
        </div>
        <div className="heading-actions">
          <div className="segment-control">
            {(['month', 'quarter', 'year'] as PeriodMode[]).map((mode) => <button key={mode} type="button" className={periodMode === mode ? 'active' : ''} onClick={() => onPeriodMode(mode)}>{periodLabel[mode]}</button>)}
          </div>
          <button className="secondary-button" type="button" onClick={onOpenTargets}><Settings2 size={16} /> 目标设置</button>
          <button className="secondary-button" type="button" onClick={exportCurrent}><Download size={16} /> 下载 KPI</button>
        </div>
      </section>

      <section className="progress-grid">
        <ProgressCard eyebrow={`${periodLabel[periodMode]} KPI 01`} title="总销售额" actual={formatCompactCurrency(summary.sales)} target={formatCompactCurrency(salesTarget)} progress={salesRate} icon={<WalletCards size={18} />} color="#2563eb" />
        <ProgressCard eyebrow={`${periodLabel[periodMode]} KPI 02`} title="重点单品数量" actual={`${formatNumber(summary.targetProductQuantity)} 盒`} target={`${formatNumber(productTarget)} 盒`} progress={productRate} icon={<PackageCheck size={18} />} color="#18a394" hint={targets.targetProduct} />
        <ProgressCard eyebrow={`${periodLabel[periodMode]} KPI 03`} title="活跃终端" actual={`${formatNumber(summary.terminals)} 家`} target={`${formatNumber(targets.activeTerminalTarget)} 家`} progress={terminalRate} icon={<Activity size={18} />} color="#7868e6" />
      </section>

      <section className="dashboard-grid dashboard-grid-even">
        <article className="panel">
          <div className="panel-head"><div><span className="eyebrow">达成对比</span><h3>三项 KPI 完成度</h3></div><span className="panel-badge">目标 = 100%</span></div>
          <Chart option={completionOption} className="chart-tall" ariaLabel="三项核心KPI达成率横向条形图" />
        </article>
        <article className="panel">
          <div className="panel-head"><div><span className="eyebrow">过程跟进</span><h3>销售额与均衡进度</h3></div></div>
          <Chart option={salesTrendOption} className="chart-tall" ariaLabel="各时间段实际销售额与均衡目标对比图" />
        </article>
      </section>

      <section className="kpi-note-grid">
        <article><span className="note-index blue"><Target size={17} /></span><div><strong>总销售额差距</strong><p>{salesTarget - summary.sales > 0 ? `距离目标还差 ${formatCompactCurrency(salesTarget - summary.sales)}` : `已超额 ${formatCompactCurrency(summary.sales - salesTarget)}`}</p></div></article>
        <article><span className="note-index teal"><PackageCheck size={17} /></span><div><strong>重点单品差距</strong><p>{productTarget - summary.targetProductQuantity > 0 ? `还需完成 ${formatNumber(productTarget - summary.targetProductQuantity)} 盒` : `已超额 ${formatNumber(summary.targetProductQuantity - productTarget)} 盒`}</p></div></article>
        <article><span className="note-index violet"><Building2Icon /></span><div><strong>终端覆盖差距</strong><p>{targets.activeTerminalTarget - summary.terminals > 0 ? `还需激活 ${formatNumber(targets.activeTerminalTarget - summary.terminals)} 家终端` : `已超过目标 ${formatNumber(summary.terminals - targets.activeTerminalTarget)} 家`}</p></div></article>
      </section>
    </div>
  );
}

function Building2Icon() {
  return <Activity size={17} />;
}
